/*
 * The Belgian Brewer: live opening status and "today" in the hours tables.
 * Reads each venue's opening_hours JSON (from the venue metaobject) off data attributes:
 *   [data-tbb-status]  + data-tbb-hours='{...}'  -> "Open now, until 23:00", "Opens today at 16:30", ...
 *   table[data-tbb-hours-table] rows with data-day="mon".."sun" -> the row for today gets .is-today
 * Times are worked out in Europe/London whatever the visitor's own timezone. Refreshes every minute.
 * The wording comes from data-tbb-words on the element (set in Liquid), with English fallbacks.
 */
const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const FALLBACK_WORDS = {
  open_until: 'Open now, until [time]',
  late: 'late',
  opens_today: 'Opens today at [time]',
  closed_now: 'Closed now',
  today_note: '[note] today',
  opens_on: '[prefix] · opens [day] at [time]',
  tomorrow: 'tomorrow',
};

const toMinutes = (time) => {
  const [h, m] = String(time).split(':').map(Number);
  return h * 60 + (m || 0);
};

function londonNow() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const dow = DAYS.indexOf(String(get('weekday')).slice(0, 3).toLowerCase());
  return { dow, mins: Number(get('hour')) * 60 + Number(get('minute')) };
}

const isOpenDay = (day) => Boolean(day && day.open);
const closeMinutes = (day) => (day.close ? toMinutes(day.close) : 24 * 60);

function statusFor(hours, words, now) {
  const w = { ...FALLBACK_WORDS, ...words };
  const fill = (text, map) => Object.entries(map).reduce((t, [k, v]) => t.replace(`[${k}]`, v), text);
  const today = hours[DAYS[now.dow]];

  if (isOpenDay(today)) {
    const opens = toMinutes(today.open);
    if (now.mins >= opens && now.mins < closeMinutes(today)) {
      const time = today.close_label === 'late' ? w.late : today.close_label || today.close || w.late;
      return { open: true, text: fill(w.open_until, { time }) };
    }
    if (now.mins < opens) return { open: false, text: fill(w.opens_today, { time: today.open }) };
  }

  for (let i = 1; i <= 7; i++) {
    const index = (now.dow + i) % 7;
    const next = hours[DAYS[index]];
    if (!isOpenDay(next)) continue;
    const note = !isOpenDay(today) && today?.closed_note && today.closed_note !== 'Closed' ? today.closed_note : '';
    const prefix = note ? fill(w.today_note, { note }) : w.closed_now;
    const day = i === 1 ? w.tomorrow : DAY_NAMES[index];
    return { open: false, text: fill(w.opens_on, { prefix, day, time: next.open }) };
  }
  return null;
}

const parse = (text) => {
  try {
    return JSON.parse(text || '{}') || {};
  } catch {
    return {};
  }
};

function paint() {
  const now = londonNow();
  if (now.dow < 0) return;

  document.querySelectorAll('[data-tbb-status][data-tbb-hours]').forEach((el) => {
    const status = statusFor(parse(el.dataset.tbbHours), parse(el.dataset.tbbWords), now);
    if (!status) return;
    el.textContent = status.text;
    el.classList.toggle('is-open', status.open);
  });

  document.querySelectorAll('[data-tbb-hours-table]').forEach((table) => {
    table.querySelectorAll('tr[data-day]').forEach((row) => {
      const isToday = row.dataset.day === DAYS[now.dow];
      row.classList.toggle('is-today', isToday);
      const flag = row.querySelector('.tbb-today-flag');
      if (flag) flag.hidden = !isToday;
    });
  });
}

paint();
setInterval(paint, 60000);
document.addEventListener('shopify:section:load', paint);
